
export const FASTWMS_VARIABLES = [
  'name',
  'brand',
  'article',
  'sellerArticle',
  'color',
  'size',
  'techSize',
  'gtin',
  'serial',
  'barcode',
  'number',
  'composition',
  'country',
  'manufacturer',
  'manufactureDate',
  'sellerName',
  'sellerShortName',
  'userId',
  'shortAddress',
  'address',
  'storageGroup',
] as const;

export type FastwmsVariable = (typeof FASTWMS_VARIABLES)[number];

export interface FastwmsLabelData {
  label: Record<FastwmsVariable, string>;
}

const LEGACY_BINDING_MAP: Record<string, FastwmsVariable> = {
  'product.name': 'name',
  'product.article': 'article',
  'product.sellerArticle': 'sellerArticle',
  'product.color': 'color',
  'product.brand': 'brand',
  'product.size': 'size',
  'product.techSize': 'techSize',
  'product.barcode': 'barcode',
  'product.orderNumber': 'number',
  'product.kiz': 'serial',
  'product.composition': 'composition',
  'product.country': 'country',
  'product.userId': 'userId',
  'product.sellerName': 'sellerName',
  'product.sellerShortName': 'sellerShortName',
  'product.date': 'manufactureDate',
};

export function variableToToken(variable: FastwmsVariable): string {
  return `{{${variable}}}`;
}

export function tokenToVariable(token: string): FastwmsVariable | null {
  const match = token.match(/^\{\{\s*([a-zA-Z0-9_]+)\s*\}\}$/);
  if (!match) return null;
  const key = match[1] as FastwmsVariable;
  return FASTWMS_VARIABLES.includes(key) ? key : null;
}

export function bindingToVariable(binding: string): FastwmsVariable | null {
  if (binding === 'label.labelNumber') return 'number';
  if (binding.startsWith('label.')) {
    const key = binding.slice('label.'.length) as FastwmsVariable;
    return FASTWMS_VARIABLES.includes(key) ? key : null;
  }
  return LEGACY_BINDING_MAP[binding] ?? null;
}

export function bindingToToken(binding: string): string {
  if (binding === 'label.sizeRuCombined') return '{{size}}-{{techSize}}';
  const variable = bindingToVariable(binding);
  return variable ? variableToToken(variable) : '';
}

/** Размер + рос. размер: без лишнего дефиса, если тех. размер не задан. */
export function formatSizeRuCombined(label: FastwmsLabelData['label']): string {
  const size = (label.size ?? '').trim();
  const ru = (label.techSize ?? '').trim();
  if (size && ru && size !== ru) return `${size}-${ru}`;
  return ru || size;
}

export function renderFastwmsTemplate(raw: string, data: FastwmsLabelData): string {
  if (!raw) return '';
  const sizeCombined = formatSizeRuCombined(data.label);
  let source = String(raw)
    .replace(/\{\{\s*sizeRuCombined\s*\}\}/g, sizeCombined)
    .replace(/\{\{\s*size\s*\}\}\s*-\s*\{\{\s*techSize\s*\}\}/g, sizeCombined);
  const withoutConditionals = source.replace(
    /\{%\s*if\s+label\.([a-zA-Z0-9_]+)\s*%\}([\s\S]*?)\{%\s*endif\s*%\}/g,
    (_, field: string, content: string) => {
      const resolvedField = field === 'labelNumber' ? 'number' : field === 'sizeRuCombined' ? 'size' : field;
      const key = resolvedField as FastwmsVariable;
      if (!FASTWMS_VARIABLES.includes(key)) return '';
      return data.label[key] ? content : '';
    },
  );
  return withoutConditionals.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key: string) => {
    const resolvedKey = key === 'labelNumber' ? 'number' : key === 'sizeRuCombined' ? 'size' : key;
    const variable = resolvedKey as FastwmsVariable;
    if (!FASTWMS_VARIABLES.includes(variable)) return '';
    return data.label[variable] ?? '';
  });
}

export function validateFastwmsTemplate(raw: string): string[] {
  if (!raw) return [];
  const unknown = new Set<string>();
  const varMatches = raw.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g);
  for (const match of varMatches) {
    const normalized = match[1] === 'labelNumber' ? 'number' : match[1] === 'sizeRuCombined' ? 'size' : match[1];
    const key = normalized as FastwmsVariable;
    if (!FASTWMS_VARIABLES.includes(key)) unknown.add(match[1]);
  }
  const ifMatches = raw.matchAll(/\{%\s*if\s+label\.([a-zA-Z0-9_]+)\s*%\}/g);
  for (const match of ifMatches) {
    const normalized = match[1] === 'labelNumber' ? 'number' : match[1] === 'sizeRuCombined' ? 'size' : match[1];
    const key = normalized as FastwmsVariable;
    if (!FASTWMS_VARIABLES.includes(key)) unknown.add(match[1]);
  }
  return Array.from(unknown);
}
